import { Component, EventEmitter, Input, Output } from '@angular/core';
import { BehaviorSubject, Observable } from "rxjs";
import { Merchant } from "../../../../business/interfaces/Merchant";
import { ErrorHandling } from "../../../../common/classes/ErrorHandling";
import {
  ActionRow,
  FilterTable,
  TableColumnInfo,
  TableConfiguration
} from "../../../../components/table-view/table-view.component";
import { Website } from "../../../../business/interfaces/Website";
import { ActivatedRoute } from "@angular/router";
import { AppNavigator, NavigatorTarget } from "../../../../services/app-navigator";
import { MerchantAPI } from "../../../../business/apis/MerchantAPI";
import { TableAction } from "../../../../components/table-view/enum/TableAction";
import { WebsiteNameId } from "../../../../business/name-id/WebsiteNameId";
import { InputField } from "../../../interfaces/InputField";
import { getEnumFromValue } from "../../../../common/utilities/General";
import { isFormSubmitted, resetForm } from "../../../../common/utilities/FormsHelper";
import { InputFieldType } from "../../../../components/input/enum/InputFieldType";
import { FormGroup, Validators } from "@angular/forms";
import { UrlValidator } from "../../../../components/input/validators/url-validator";
import { Image } from "../../../../business/interfaces/Image";
import { MediaType } from "../../../../business/enums/MediaType";

@Component({
  selector: 'merchant-videos',
  templateUrl: './merchant-videos.component.html',
  styleUrls: ['./merchant-videos.component.css']
})
export class MerchantVideosComponent {

  @Input() loadData: BehaviorSubject<any> | null = null;
  @Output() reloadData = new EventEmitter<string>();

  merchantDetails: Merchant | null = null;

  form: FormGroup = new FormGroup({});

  videoFields: InputField[] = [];
  urlItem!: InputField;

  tableConfiguration: TableConfiguration<Website> = {};
  loadTableData: BehaviorSubject<[any, any]> = new BehaviorSubject<[any, any]>([[], null]);


  isScreenLoading: boolean = false;
  isLoading = false;
  errorHandling: ErrorHandling | null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private appNavigator: AppNavigator,
    private merchantApi: MerchantAPI
  ) {
  }

  ngOnInit(): void {
    this.videoFields = this.getInputFields();

    this.tableConfiguration = {
      hasFilter: false,
      tableColumnInfo: this.getColumTableInfo(),
      actionsRow: this.getActionRowTable.bind(this),
      getRowValue: this.getRowValue.bind(this),
      onCellTapped: this.onCellTapped.bind(this),
      loadData: this.loadTableData,
      reloadDataTable: this.reloadDataTable.bind(this),
    };

    this.loadData!.subscribe((data) => {
      this.merchantDetails = data;
      this.reloadDataViews();
      this.isScreenLoading = false;
      this.isLoading = false;
    });
  }

  ngOnDestroy(): void {
    this.loadTableData?.unsubscribe();
  }

  getInputFieldById(id: WebsiteNameId): InputField | undefined {
    let item = this.videoFields.find((field) => field.id === id);
    return item;
  }

  updateInputFieldById(id: string, value: any) {
    let fieldId = getEnumFromValue(WebsiteNameId, id) as WebsiteNameId;
    let field = this.getInputFieldById(fieldId);
    if (field != undefined) {
      field.value = value;
    }
  }

  getValueFieldById(id: string): Object {
    let fieldId = getEnumFromValue(WebsiteNameId, id) as WebsiteNameId;
    let field = this.getInputFieldById(fieldId);
    return field?.value ?? null;
  }

  restError() {
    this.errorHandling = null;
  }

  submit() {
    if (isFormSubmitted(this.form)) {
      this.saveMerchantWebsite();
    }
  }

  saveMerchantWebsite() {
    this.restError();
    this.isLoading = true;
    let merchantId = this.merchantDetails?.id ?? "";

    let url = this.getValueFieldById(WebsiteNameId.URL)?.toString()
    this.merchantApi
      .addVideo({
        merchantId: merchantId,
        type: MediaType.VIDEO,
        url: url,
      })
      .subscribe({
        next: (item) => {
          this.reloadData!.emit(this.merchantDetails?.id);
          this.resetFields()
        },
        error: (error: any) => {
          this.checkError(error);
          this.isLoading = false;
        },
      });
  }

  checkError(error: any) {
    let errorHandling = ErrorHandling.configurationError(error);
    this.errorHandling = errorHandling;
  }

  reloadDataViews() {
    let list = this.merchantDetails?.images ?? [];
    list = list.filter(item => item.type === MediaType.VIDEO)
    this.tableConfiguration.loadData?.next([list, null]);
  }

  getActionRowTable<T>(item: T): ActionRow[] {
    let deleteItem = {
      title: "Delete",
      type: "btn btn-danger",
      iconClass: "bi bi-trash",
      actionType: TableAction.REMOVE,
      onTapAction: this.onTapAction.bind(this),
    };
    return [deleteItem]
  }

  getColumTableInfo(): TableColumnInfo[] {


    let URL = {
      columId: WebsiteNameId.URL,
      name: "Video Url",
    };

    return [
      URL,
    ];
  }

  onCellTapped<T>(column: TableColumnInfo, item: T) {
    const id = column.columId;
    if (id == WebsiteNameId.ID) {
      this.onTapImageDetails(item);
    }
  }

  getRowValue<T>(column: TableColumnInfo, item: T): any {
    const id = column.columId;
    let url = item as Image;
    let value = url[id as keyof Image];
    let styleClass = null;

    return {
      value: value,
      class: styleClass,
    };
  }

  navigateToCreateNewItem() {
    this.appNavigator.navigateTo({
      target: NavigatorTarget.merchantWebsiteDetails,
      id: this.merchantDetails?.id,
    });
  }

  private getInputFields(): InputField[] {
    this.urlItem = {
      id: WebsiteNameId.URL,
      inputFieldType: InputFieldType.textField,
      floatingLabel: "Video Url",
      value: "",
      validators: [Validators.required, new UrlValidator().validator],
      loadData: new BehaviorSubject<any>(""),
    } as InputField;

    return [this.urlItem];
  }

  private resetFields() {
    for (const field of this.videoFields) {
      field.loadData?.next("");
    }
    resetForm(this.form)
  }

  private onTapAction<T>(action: any, item: T) {
    let WebsiteAction = action as TableAction;
    let media = item as Image;
    let call: Observable<any>;

    if (WebsiteAction == TableAction.REMOVE) {
      let merchantId = this.merchantDetails?.id ?? ""
      let imageId = media.id
      call = this.merchantApi.deleteImage({ merchantId: merchantId, imageId: imageId });
    } else {
      return;
    }

    this.isScreenLoading = true;
    call.subscribe({
      next: () => {
        this.reloadData!.emit(this.merchantDetails?.id);
      },
      error: (error: any) => {
      },
    });
  }

  private onTapImageDetails<T>(item: T) {
  }

  private reloadDataTable<T>(filterData: FilterTable) {
  }
}
